import { formatNumber } from '@metapic-apps/shared'

import { config } from '@/lib/helpers/config.helpers'

export const round = (number: number, precision = 0) => {
  const factor = Math.pow(10, precision)
  return Math.round(number * factor) / factor
}

type ArrayObject = Record<string, unknown[]>

export const hasItems = <T extends ArrayObject>(obj: T): boolean => {
  for (const key in obj) {
    const val = obj[key]
    if (val && val.length > 0) {
      return true
    }
  }
  return false
}

export const camelToSnakeCase = (str: string) =>
  str.replace(/[A-Z]/g, (letter: string) => `_${letter.toLowerCase()}`)

type DebounceLeadingOptions = { leading: true; trailing?: false }
type DebounceTrailingOptions = { leading?: false; trailing: true }
type DebounceBothOptions = { leading: true; trailing: true }
type DebounceOptions = DebounceLeadingOptions | DebounceTrailingOptions | DebounceBothOptions
export function debounce<T extends (...args: never[]) => void>(
  fn: T,
  delay: number,
  { leading = false, trailing = true }: DebounceOptions = { trailing: true },
): T {
  let timeoutID: ReturnType<typeof setTimeout> | undefined
  return function (this: never, ...args: never[]) {
    const callNow = leading && !timeoutID
    clearTimeout(timeoutID)
    timeoutID = setTimeout(() => {
      timeoutID = undefined
      if (trailing) {
        fn.apply(this, args)
      }
    }, delay)
    if (callNow) {
      fn.apply(this, args)
    }
  } as T
}

// The value is a number in the range from 0 to 1
export const convertToPercentage = (value: number): string =>
  `${formatNumber(value * 100, 'compact', 'decimal', 2)}%`

export const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

export const validateUrl = (url: string) => {
  const urlRegex = /^https:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/
  return urlRegex.test(url)
}

export const isValidEmail = (email: string) => {
  const regex = /^[\S]+@[\S]+\.[a-zA-Z]+$/
  return regex.test(email)
}

export const isValidIdentifier = (identifier: string) => {
  const regex = /^[@\w](?!.*?\.{2})[\w.]{1,28}[\w]$/
  return regex.test(identifier)
}

export const isValidPhone = (phoneNumber: string) => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return false
  }

  const cleaned = phoneNumber.replace(/\s/g, '')
  const e164Regex = /^\+[1-9]\d{4,14}$/
  return e164Regex.test(cleaned)
}

export const exhaustiveGuard = (_value: never): never => {
  throw new Error(
    `ERROR! Reached forbidden guard function with unexpected value: ${JSON.stringify(_value)}`,
  )
}

export const redirectAndLoginAdvertiserUrl = (advertiserId: number, redirectPath?: string) => {
  const url = new URL(`${config.advertiserAppUrl}/login`)
  url.searchParams.append('store_id', advertiserId.toString())

  if (redirectPath) {
    url.searchParams.append('redirect', redirectPath)
  }

  return url.toString()
}
